/**
 * loadStages.js — этапы загрузки каталога и правило «кто виноват». [10.19.23]
 *
 * ПОВОД. С живой машины пришли числа: в 10.19.8 каталог на 5000 срезов от выбора
 * папки до картинки занимал 185 с, сейчас — только обход больше 200 с, потом
 * парсинг с регистрацией около 500 с. Я сверил обе сборки файл за файлом. Весь
 * путь загрузки — `loaders/loaderManager.js`, `loaders/dicomParseWorker.js`,
 * `loaders/workerPool.js`, `loaders/loaderWorkerBridge.js`, `loaders/sliceSort.js`,
 * `managers/DatasetManager.js`, `managers/MemoryGuardian.js`,
 * `storage/dicomStore.js`, `core/streamingManager.js` — совпадает ПОБАЙТОВО, а в
 * `ui/fileOps.js` единственное отличие в загрузке — перевод одной надписи с
 * английского. Ни новых наблюдателей за DOM, ни новых периодических таймеров, ни
 * смены версий Cornerstone: набор зависимостей тот же.
 *
 * Значит замедление пришло не из кода, и продолжать угадывать бессмысленно.
 * Дальше — только замер, и замер ПО ЭТАПАМ: «загрузка стала медленнее» не
 * называет виновника, а «чтение файла 47 мс, парсинг 3 мс» называет сразу.
 *
 * ЧТО РАЗДЕЛЯЕТСЯ:
 *   обход      — браузер собирает список файлов. Наш код в это время не работает
 *                вовсе, и ускорить его мы не можем. Зато можем показать, что
 *                минуты ушли ИМЕННО СЮДА.
 *   чтение     — File.slice(...).arrayBuffer(): байты идут от поставщика
 *                хранилища. [10.19.33] Живой замер владельца (18.6 мс/файл с
 *                ВНУТРЕННЕЙ памяти телефона) похоронил прежнюю догму этой
 *                шапки «внутренняя = микросекунды, внешняя = десятки мс»: на
 *                Android КАЖДОЕ открытие файла — отдельное межпроцессное
 *                обращение к поставщику, и десятки миллисекунд НА ФАЙЛ — цена
 *                самого канала, какой бы носитель за ним ни стоял; SD-карта,
 *                USB-OTG или облако лишь добавляют сверху. По числу носитель
 *                НЕ ОПРЕДЕЛИТЬ — и плашка больше не делает вид, что может.
 *                На 5000 файлов эти миллисекунды — минуты; лечит их одно при
 *                любом носителе: ZIP-архив, одно обращение вместо тысяч.
 *   парсинг    — разбор заголовка в рабочем потоке. Считает процессор.
 *   регистрация— заведение снимков в Cornerstone.
 *   сортировка — раскладка срезов по геометрии.
 *   первыйКадр — от конца регистрации до первой картинки на экране.
 *
 * ЧЕТЫРЕ СЧЁТЧИКА ПАТОЛОГИЙ, каждый — отдельная гипотеза, которую замер
 * подтверждает или снимает:
 *   повторныеЧтения — файл прочитан второй раз бо́льшим куском, потому что
 *                     заголовок не влез в первый (на телефоне первый кусок
 *                     128 КБ). Это УДВАИВАЕТ обращения к хранилищу, и на данных
 *                     с большими частными тегами или встроенными эскизами так
 *                     будет с каждым файлом.
 *   таймауты        — разбор файла не ответил за 12 с. Каждый такой файл сам по
 *                     себе стоит 12 с ожидания.
 *   откатыВПоток    — файл не смог разобрать рабочий поток, разбирал главный.
 *   ядер/потоков    — сколько рабочих потоков реально создано и сколько ядер
 *                     отдал браузер. При энергосбережении и перегреве Android
 *                     занижает hardwareConcurrency, и пул съёживается до одного
 *                     потока — при том же коде.
 *
 * Файл без импортов и без DOM: правило проверяется тестом, а не запуском
 * программы на телефоне.
 */

/** Порядок важен: в этом порядке этапы и показываются. */
// [10.19.34] Поколение ФОРМУЛИРОВОК этого файла. Летопись 10.19.6 ввела
// версию сборки в плашку, чтобы по скрину было видно, какая программа
// запущена. Сегодня владелец показал следующий слой той же беды: его
// локальная ветка носила номер 10.19.33 ДО моего выпуска — по скрину «версия
// 10.19.33» невозможно было понять, ЧЬИ тексты говорят. Метка ниже меняется
// при каждой правке вердиктов и уезжает в плашку рядом с версией сборки:
// «версия X · тексты Y» — расхождение поколений видно с одного взгляда.
export const ПОКОЛЕНИЕ_ФОРМУЛИРОВОК = '10.19.37'; // советам дописан обратный путь: «Экспорт ZIP»

export const ЭТАПЫ = Object.freeze(['обход', 'чтение', 'парсинг', 'регистрация', 'сортировка', 'первыйКадр']);

/** Человеческие названия и короткое «что это значит». */
export const ПОЯСНЕНИЯ = Object.freeze({
  обход: 'браузер собирал список файлов (наш код ещё не работал)',
  чтение: 'байты шли от поставщика хранилища',
  парсинг: 'разбор заголовков в рабочих потоках',
  регистрация: 'заведение снимков в Cornerstone',
  сортировка: 'раскладка срезов по геометрии',
  первыйКадр: 'до первой картинки на экране',
});

/**
 * Кто виноват: этап, забравший больше всего времени, и во сколько раз он
 * обошёл следующий за ним. Соотношение важнее самих секунд: если один этап
 * съел половину всего времени, спорить больше не о чем.
 *
 * @param {Record<string, number>} мс — секунды/миллисекунды по этапам
 * @returns {{этап: string|null, мс: number, доля: number, обошёлВРаз: number, всего: number}}
 */
export function узкоеМесто(мс = {}) {
  const пары = ЭТАПЫ.map((э) => [э, Number(мс[э]) || 0]).filter(([, v]) => v > 0);
  const всего = пары.reduce((с, [, v]) => с + v, 0);
  if (!пары.length || !(всего > 0)) return { этап: null, мс: 0, доля: 0, обошёлВРаз: 0, всего: 0 };
  пары.sort((a, b) => b[1] - a[1]);
  const [этап, значение] = пары[0];
  const второй = пары[1]?.[1] || 0;
  return {
    этап,
    мс: значение,
    доля: значение / всего,
    обошёлВРаз: второй > 0 ? значение / второй : Infinity,
    всего,
  };
}

/**
 * Замечания по счётчикам — то, что можно сказать ДО всякого обсуждения.
 * Каждое замечание называет число и следствие, а не «возможно, есть проблема».
 *
 * @param {{файлов?: number, повторныеЧтения?: number, таймауты?: number,
 *          откатыВПоток?: number, потоков?: number, ядер?: number,
 *          мс?: Record<string, number>}} з
 * @returns {string[]}
 */
export function замечания(з = {}) {
  const из = [];
  const файлов = Number(з.файлов) || 0;
  const мс = з.мс || {};

  const повт = Number(з.повторныеЧтения) || 0;
  if (файлов && повт / файлов > 0.2) {
    из.push(`повторных чтений ${повт} из ${файлов} файлов (${Math.round(повт / файлов * 100)}%): `
      + 'заголовок не влезает в первый кусок, и каждый такой файл читается из хранилища ДВА раза');
  }

  const тмо = Number(з.таймауты) || 0;
  if (тмо > 0) {
    из.push(`таймаутов разбора ${тмо}: каждый стоит 12 с ожидания, вместе это ${Math.round(тмо * 12)} с`);
  }

  const отк = Number(з.откатыВПоток) || 0;
  if (файлов && отк / файлов > 0.05) {
    из.push(`в главном потоке разобрано ${отк} файлов из ${файлов}: рабочие потоки с ними не справились`);
  }

  const потоков = Number(з.потоков) || 0;
  const ядер = Number(з.ядер) || 0;
  if (потоков === 1 && файлов > 200) {
    из.push(`рабочий поток всего один (браузер отдал ядер: ${ядер || '?'}), поэтому парсинг идёт последовательно — `
      + 'так бывает при энергосбережении и перегреве, код тут не менялся');
  }

  // [10.19.33] Латентность канала — из ЗАНЯТОСТИ (сумма), не из окна: при
  // параллельных чтениях окно меньше суммы, и «мс на файл» из окна врало бы
  // в лучшую сторону ровно во столько раз, какова глубина.
  // [10.19.36] И ЭТА догма похоронена замером владельца: занятость/файлов
  // дала 221 мс при глубине 9.7 на ТРЁХ воркерах — три потока не читают
  // вдесятером. Внутри воркера await arrayBuffer отпускает цикл, Comlink
  // запускает следующий вызов, и каждый интервал «чтения» меряет СВОЮ
  // задержку ВМЕСТЕ с очередью соседей к общему поставщику. Сумма = задержка
  // × наша же очередь; окна чтения у воркерного пути нет вовсе (добавить()
  // пишет одно значение в окно И в сумму). Честная пропускная цена канала —
  // СТЕНА ПУЛА ÷ файлы (у владельца: 22.7 и 38.0 мс), очередь = сумма ÷
  // стена, задержка одного запроса = сумма ÷ файлы. Вердикты ниже говорят
  // все три числа по именам.
  const занятостьЧтения = Number((з.сумма || {}).чтение) || Number(мс.чтение) || 0;
  const _окноПула = Number(з.окноПулаМс) || 0;
  const _воркерное = Number(з.чтенийВВоркере) > 0;
  if (файлов && _воркерное && _окноПула > 0 && _окноПула / файлов > 15) {
    const цена = _окноПула / файлов;
    const очередь = занятостьЧтения / _окноПула;
    const задержка = занятостьЧтения / файлов;
    из.push(`канал пропускает один файл за ${цена.toFixed(1)} мс — столько стоит путь до поставщика хранилища `
      + `(стена чтения ${(_окноПула / 1000).toFixed(1)} с на ${файлов} файлов; на телефоне это касается и внутренней памяти, `
      + 'SD-карта, USB-OTG или облако лишь добавляют сверху). '
      + `В очереди к поставщику держалось в среднем ${очередь.toFixed(1)} запросов, отчего задержка каждого — ${задержка.toFixed(0)} мс. `
      + `Быстрее всего — упаковать исследование в ZIP и открыть через «Файл → Импорт ZIP»: одно обращение вместо ${файлов}; уже загруженное можно сохранить архивом на будущее: «Файл → Экспорт ZIP»`);
  } else if (файлов && !_воркерное && занятостьЧтения / файлов > 15) {
    // Невоеркерный путь: интервалы честные (токены v2), очередь в них не сидит.
    из.push(`чтение одного файла ${(занятостьЧтения / файлов).toFixed(1)} мс — обращение к поставщику `
      + 'хранилища за каждым файлом (на телефоне это касается и внутренней памяти; SD-карта, USB-OTG или облако лишь добавляют). '
      + `Быстрее всего — упаковать исследование в ZIP и открыть через «Файл → Импорт ZIP»: одно обращение вместо ${файлов}; уже загруженное можно сохранить архивом на будущее: «Файл → Экспорт ZIP»`);
  }
  {
    const окно = Number(мс.чтение) || 0;
    const глубина = окно > 0 ? занятостьЧтения / окно : 0;
    if (файлов > 200 && занятостьЧтения / файлов > 15 && глубина > 0 && глубина < 1.5 && !Number(з.чтенийВВоркере)) {
      из.push(`фактическая глубина чтения ~${глубина.toFixed(1)}: запросы к хранилищу исполняются ПО ОДНОМУ, `
        + 'сколько бы их ни стояло в очереди — поставщик отдаёт последовательно. Из программы быстрее не прочитать; '
        + `ZIP-архив («Файл → Импорт ZIP») превращает ${файлов} обращений в одно и убирает узкое место при любом носителе; уже загруженное можно сохранить архивом на будущее: «Файл → Экспорт ZIP»`);
    } else if (глубина >= 2.5) {
      из.push(`чтение шло с фактической глубиной ~${глубина.toFixed(1)} — канал хранилища держит очередь, узкое место не здесь`);
    }
  }
  {
    const окноПула = Number(з.окноПулаМс) || 0;
    const потоков = Number(з.потоков) || 0;
    if (окноПула > 3000 && потоков >= 2 && Number(з.чтенийВВоркере) > 0 && занятостьЧтения >= окноПула * 0.8) {
      // [10.19.36] Было: «поставщик отдаёт данные последовательно на весь
      // процесс» — вывод сильнее данных: глубины 3.5 и 9.7 из замеров
      // владельца показывают, что интервалы перекрываются. Верное и
      // доказуемое: поставщик ОБЩИЙ на процесс, и наша очередь к нему растёт
      // с потоками, а стена — нет.
      из.push(`канал хранилища насыщен: занятость чтения ${(занятостьЧтения / 1000).toFixed(1)} с при стене пула ${(окноПула / 1000).toFixed(1)} с `
        + `и ${потоков} потоках — средняя очередь к поставщику ${(занятостьЧтения / окноПула).toFixed(1)} запросов. `
        + 'Поставщик общий на весь процесс: добавка потоков удлинит очередь, а стену не сожмёт. '
        + 'Программа читает минимально возможное; выход — один ZIP-архив вместо тысяч файлов («Файл → Импорт ZIP»); уже загруженное можно сохранить архивом на будущее: «Файл → Экспорт ZIP»');
    }
  }

  if (файлов && (Number(мс.обход) || 0) / файлов > 20) {
    из.push(`обход каталога ${((Number(мс.обход) || 0) / файлов).toFixed(1)} мс на файл — это делает браузер до нашего кода; `
      + 'на компьютере обычно меньше пяти, на телефоне дороже при любом носителе'); // [10.19.33] было «на внутренней памяти» — та же похороненная догма
  }

  return из;
}

/**
 * Сводка одной строкой на экран: этапы с числами, узкое место и замечания.
 * Возвращает СТРОКИ, а не HTML — показывает их тот, кто зовёт.
 */
export function сводка(з = {}) {
  const мс = з.мс || {};
  const файлов = Number(з.файлов) || 0;
  const воркерноеЧтение = Number(з.чтенийВВоркере) > 0;
  const части = ЭТАПЫ
    .filter((э) => (Number(мс[э]) || 0) > 0)
    .map((э) => `${э} ${(Number(мс[э]) / 1000).toFixed(1)} с${э === 'чтение' && воркерноеЧтение ? ' (суммарно по потокам)' : ''}`);
  const у = узкоеМесто(мс);
  const строки = [];
  if (части.length) строки.push(`Этапы: ${части.join(' · ')}`);
  if (у.этап) {
    строки.push(`Больше всего времени — ${у.этап}: ${(у.мс / 1000).toFixed(1)} с `
      + `(${Math.round(у.доля * 100)}% всего), ${ПОЯСНЕНИЯ[у.этап] || ''}`);
  }
  if (файлов && у.всего) строки.push(`Файлов ${файлов}, этапы суммарно ${(у.всего / 1000).toFixed(1)} с, ${(у.всего / файлов).toFixed(1)} мс на файл`
    + (Number(з.окноПулаМс) > 0 ? `; стена пула ${(Number(з.окноПулаМс) / 1000).toFixed(1)} с` : ''));
  const пул = [];
  if (з.потоков) пул.push(`рабочих потоков ${з.потоков}`);
  if (з.ядер) пул.push(`ядер отдано ${з.ядер}`);
  if (з.повторныеЧтения) пул.push(`повторных чтений ${з.повторныеЧтения}`);
  if (з.таймауты) пул.push(`таймаутов ${з.таймауты}`);
  if (з.откатыВПоток) пул.push(`разобрано главным потоком ${з.откатыВПоток}`);
  if (з.чтенийВВоркере) пул.push(`чтений в воркерах ${з.чтенийВВоркере}`);
  if (пул.length) строки.push(пул.join(' · '));
  строки.push(...замечания({ ...з, мс }));
  return строки;
}

/**
 * Секундомер по этапам. Живёт один на загрузку; пересоздаётся каждый раз, чтобы
 * числа прошлой загрузки не примешивались к нынешней.
 */
export function секундомер(часы = () => Date.now()) {
  const мс = {};      // ОКНО этапа: сколько шла стена, пока этап был открыт хотя бы раз
  const сумма = {};   // ЗАНЯТОСТЬ: все интервалы по отдельности, наложение складывается
  const счёт = { файлов: 0, повторныеЧтения: 0, таймауты: 0, откатыВПоток: 0, потоков: 0, ядер: 0, байт: 0, чтенийВВоркере: 0, окноПулаМс: 0 };
  const глубина = {};
  const окноСтарт = {};
  const последний = {};
  return {
    /**
     * Открыть этап. Возвращает функцию-СТОП своего интервала.
     *
     * [10.19.33] Прежний секундомер затирал старт при повторном «начать» и
     * терял «кончить» после чужого закрытия — при десяти чтениях в полёте
     * числа этапов были мозаикой случайных промежутков, ни стеной, ни
     * суммой. Теперь: ОКНО (мс) идёт по глубине 0→1…1→0 — это стена; СУММА —
     * по каждому интервалу, это занятость канала; их отношение — фактическая
     * глубина параллелизма, и она отличает «читаем по одному» от «читаем
     * вдесятером» без единой догадки.
     */
    начать(этап) {
      const т0 = часы();
      глубина[этап] = (глубина[этап] || 0) + 1;
      if (глубина[этап] === 1) окноСтарт[этап] = т0;
      let закрыт = false;
      const стоп = () => {
        if (закрыт) return;
        закрыт = true;
        const т1 = часы();
        сумма[этап] = (сумма[этап] || 0) + (т1 - т0);
        глубина[этап] -= 1;
        if (глубина[этап] === 0) мс[этап] = (мс[этап] || 0) + (т1 - окноСтарт[этап]);
      };
      последний[этап] = стоп;
      return стоп;
    },
    /** Закрыть последний открытый интервал этапа (одиночное использование — как раньше). */
    кончить(этап) { const с = последний[этап]; if (с) с(); },
    /** Добавить готовое время (обход браузера; тайминги из воркеров). */
    добавить(этап, значение) {
      if (Number.isFinite(значение) && значение > 0) {
        мс[этап] = (мс[этап] || 0) + значение;
        сумма[этап] = (сумма[этап] || 0) + значение;
      }
    },
    /** Увеличить счётчик. */
    считать(имя, сколько = 1) { if (имя in счёт) счёт[имя] += сколько; },
    /** Записать значение счётчика (потоки, ядра, окно пула). */
    записать(имя, значение) { if (имя in счёт) счёт[имя] = Number(значение) || 0; },
    /** Снимок для отчёта: окна, занятость и счётчики. */
    снимок() { return { мс: { ...мс }, сумма: { ...сумма }, ...счёт }; },
  };
}

export default { ЭТАПЫ, ПОЯСНЕНИЯ, узкоеМесто, замечания, сводка, секундомер };
